// Public application API contract (spec §11). Source of truth for OpenAPI and the typed web client.
import { z } from 'zod';
import {
  Id, IsoTime, CatalogKind, MediaKind, Connection, Category, CatalogItem, MovieDetail, SeriesDetail,
  Program, Freshness, UserState, UserListName, Preferences, Profile, ClientCapabilities, Capability,
  Channel, MovieSummary, SeriesSummary, Episode, Progress, Reminder,
} from './models.ts';

// ---- Session / workspace ----
export const SessionResponse = z.object({
  device: z.object({ id: Id, sharedMode: z.boolean(), createdAt: IsoTime }),
  profile: Profile,
  connections: z.array(Connection),
});
export type SessionResponse = z.infer<typeof SessionResponse>;

// ---- Connections (AUTH01-03) ----
export const ConnectRequest = z.object({
  serverUrl: z.string().min(1).max(2048),
  username: z.string().min(1).max(512).describe('Preserved exactly; never trimmed'),
  password: z.string().min(1).max(512).describe('Preserved exactly; never trimmed'),
  remember: z.boolean().default(false),
  alias: z.string().max(80).optional(),
  acknowledgeInsecure: z.boolean().default(false).describe('Required true for http:// servers'),
  connectionId: Id.optional().describe('Reconnect an existing source'),
  sharedDeviceMode: z.boolean().default(false),
});
export type ConnectRequest = z.infer<typeof ConnectRequest>;
/** App hostnames tied to one provider server: the connect form asks only for username and password, and the API
 * refuses any other server from that host. Values are canonical base URLs (parseServerUrl(v).baseUrl === v). */
export const LOCKED_SERVERS: Readonly<Record<string, string>> = { 'ultv.watchtuned.app': 'https://ultv.life:8443' };
export const ConnectResponse = z.object({ connection: Connection, jobId: z.string().nullable() });
export type ConnectResponse = z.infer<typeof ConnectResponse>;
export const ConnectionListResponse = z.object({ connections: z.array(Connection) });
export const UpdateConnectionRequest = z.object({
  alias: z.string().max(80).optional(),
  userConnectionAllowance: z.number().int().min(1).max(16).nullable().optional(),
});
export const RefreshResponse = z.object({ jobId: z.string(), revision: z.string().nullable(), coalesced: z.boolean() });

// ---- Catalog (XC04, VOD01, VOD02) ----
export const CatalogSort = z.enum(['default', 'name', 'added', 'year', 'number']);
export const CatalogQuery = z.object({
  connectionId: Id,
  kind: CatalogKind,
  category: Id.optional(),
  q: z.string().max(200).optional(),
  sort: CatalogSort.default('default'),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(250).default(100),
});
export const CatalogResponse = z.object({
  revision: Id,
  items: z.array(CatalogItem),
  nextCursor: z.string().nullable(),
  total: z.number().int().nullable(),
  freshness: Freshness,
});
export type CatalogResponse = z.infer<typeof CatalogResponse>;
export const CategoriesResponse = z.object({ revision: Id.nullable(), categories: z.array(Category) });
export type CategoriesResponse = z.infer<typeof CategoriesResponse>;
export const MovieDetailResponse = z.object({ movie: MovieDetail, freshness: Freshness });
export const SeriesDetailResponse = z.object({ series: SeriesDetail, freshness: Freshness });
export const ChannelResponse = z.object({ channel: Channel });

// ---- Guide (EPG01-03) ----
export const GuideQuery = z.object({
  connectionId: Id,
  channelIds: z.string().max(20000).describe('Comma separated, max 100'),
  from: IsoTime,
  to: IsoTime.describe('Window max 24h'),
});
export const GuideChannel = z.object({
  channelId: Id,
  mapping: z.enum(['mapped', 'unmapped', 'manual']),
  coverageFrom: IsoTime.nullable(),
  coverageTo: IsoTime.nullable(),
  programs: z.array(Program),
  pending: z.boolean().optional().describe('A provider lookup for this channel was still running when the response was sent (EPG03 fallback / XC07 past fill); ask again in a few seconds'),
});
export const GuideResponse = z.object({
  from: IsoTime,
  to: IsoTime,
  status: z.enum(['ok', 'partial', 'unavailable']),
  channels: z.array(GuideChannel),
  freshness: Freshness,
});
export type GuideResponse = z.infer<typeof GuideResponse>;

// ---- Manual EPG mapping (EPG02): exact XMLTV ids only, never fuzzy; manual beats the provider mapping ----
export const EpgMappingsQuery = z.object({
  q: z.string().max(200).optional().describe('Case-insensitive substring of an XMLTV channel id or display name'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const EpgMapping = z.object({ channelId: Id, epgChannelId: z.string().min(1).max(200).describe('Exact XMLTV channel id') });
export type EpgMapping = z.infer<typeof EpgMapping>;
export const EpgMappingsResponse = z.object({
  manual: z.array(EpgMapping),
  xmltv: z.object({
    status: z.enum(['ok', 'unavailable']),
    fetchedAt: IsoTime.nullable(),
    total: z.number().int(),
    unmapped: z.number().int().describe('XMLTV channels no channel maps to'),
    channels: z.array(z.object({ id: z.string().max(200), name: z.string().max(300).nullable(), mapped: z.boolean() })).describe('Matches for q, unmapped first'),
  }),
});
export type EpgMappingsResponse = z.infer<typeof EpgMappingsResponse>;
export const EpgMappingResponse = z.object({ mapping: EpgMapping, refreshQueued: z.boolean() });
export type EpgMappingResponse = z.infer<typeof EpgMappingResponse>;

// ---- Search (SEARCH01) ----
export const SearchQuery = z.object({
  connectionId: Id,
  q: z.string().min(1).max(200),
  types: z.string().max(100).optional().describe('Comma separated subset of live,movie,series,episode,program'),
  category: Id.optional(),
  language: z.string().max(16).optional(),
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const SearchResponse = z.object({
  query: z.string(),
  groups: z.object({
    live: z.array(Channel),
    movie: z.array(MovieSummary),
    series: z.array(SeriesSummary),
    episode: z.array(Episode),
    program: z.array(Program),
  }),
  coverage: z.object({
    episodes: z.enum(['complete', 'partial', 'none']).describe('Episodes are indexed only for series whose details were loaded'),
    programsFrom: IsoTime.nullable(),
    programsTo: IsoTime.nullable(),
  }),
  nextCursor: z.string().nullable(),
});
export type SearchResponse = z.infer<typeof SearchResponse>;

// ---- Home (HOME01) ----
export const HomeResponse = z.object({
  continueWatching: z.array(z.object({ item: z.union([MovieSummary, Episode]), progress: Progress })),
  nextEpisodes: z.array(z.object({ series: SeriesSummary, episode: Episode })),
  liveNow: z.array(z.object({ channel: Channel, program: Program.nullable() })),
  reminders: z.array(Reminder),
  pinnedChannels: z.array(Channel),
});
export type HomeResponse = z.infer<typeof HomeResponse>;

// ---- Live sports (SPORT01): today's games and the connection's channels carrying each one ----
export const SportLeague = z.enum(['mlb', 'nfl', 'nba', 'nhl', 'ncaaf', 'ncaab', 'boxing', 'mma']);
export type SportLeague = z.infer<typeof SportLeague>;
export const SportsQuery = z.object({
  connectionId: Id,
  league: SportLeague.optional().describe('Only this league; default every league'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().describe('Game day (YYYY-MM-DD) as the league defines it; default today UTC. Within ±7 days'),
});
export const SportTeam = z.object({
  id: z.string().max(32),
  name: z.string().max(80).describe('Full name, e.g. New York Mets'),
  nickname: z.string().max(40).describe('e.g. Mets'),
  abbreviation: z.string().max(8).describe('e.g. NYM'),
  exclude: z.array(z.string().max(80)).optional().describe('Folded names of other teams in the league that contain one of this team\'s tokens ("michigan state" for Michigan): a token inside one of these is not this team'),
  badge: z.string().url().max(300).optional().describe('Crest (transparent PNG) on the schedule feed\'s CDN'),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).optional().describe('Primary team colour'),
});
export type SportTeam = z.infer<typeof SportTeam>;
export const SportChannel = z.object({
  channel: Channel,
  via: z.enum(['game', 'guide', 'network', 'team']).describe('How the channel was matched, strongest first: both teams in the channel name; a guide programme naming both teams in the game window; a listed broadcaster in the channel name; one team\'s full name'),
  evidence: z.string().max(300).describe('The channel name, programme title or broadcaster that matched'),
});
export type SportChannel = z.infer<typeof SportChannel>;
export const SportEvent = z.object({
  id: z.string().max(64),
  league: SportLeague,
  start: IsoTime,
  status: z.enum(['scheduled', 'live', 'final', 'postponed']),
  detail: z.string().max(80).describe('Feed status text, e.g. In Progress. Never a score (spoiler rule)'),
  title: z.string().max(120).optional().describe('Fight card name ("UFC 331 Van vs Pantoja 2"); team games are titled "Away vs Home"'),
  card: z.string().max(60).optional().describe('Folded card token ("ufc 331") that identifies a fight card on its own'),
  timeTbd: z.boolean().optional().describe('The feed gave no start time (midnight): start is the day only'),
  art: z.string().url().max(300).optional().describe('Card art on the schedule feed\'s CDN: the fight poster or thumb (team games carry badges instead)'),
  home: SportTeam.describe('For a fight: the first-named fighter'),
  away: SportTeam.describe('For a fight: the second-named fighter'),
  broadcasters: z.array(z.string().max(80)).describe('TV broadcasters listed by the league feed, cleaned'),
  channels: z.array(SportChannel),
});
export type SportEvent = z.infer<typeof SportEvent>;
export const SportsResponse = z.object({
  league: SportLeague.optional(),
  date: z.string(),
  schedule: z.object({ status: z.enum(['ok', 'stale', 'unavailable']), fetchedAt: IsoTime.nullable() }),
  games: z.array(SportEvent),
});
export type SportsResponse = z.infer<typeof SportsResponse>;

// ---- Profile state (STORE03) ----
export const StateOperation = z.discriminatedUnion('op', [
  z.object({ op: z.literal('list.add'), list: UserListName, itemId: Id, kind: MediaKind }),
  z.object({ op: z.literal('list.remove'), list: UserListName, itemId: Id }),
  z.object({
    op: z.literal('progress.update'), itemId: Id, positionSec: z.number().nonnegative(),
    durationSec: z.number().nonnegative().nullable(), sessionId: Id, seq: z.number().int().nonnegative(),
  }),
  z.object({ op: z.literal('progress.remove'), itemId: Id }),
  z.object({ op: z.literal('watched.set'), itemId: Id, watched: z.boolean().nullable().describe('null clears manual override') }),
  z.object({ op: z.literal('reminder.add'), programId: Id, channelId: Id }),
  z.object({ op: z.literal('reminder.remove'), reminderId: Id }),
  z.object({ op: z.literal('preferences.set'), preferences: Preferences.partial() }),
  z.object({ op: z.literal('history.clear') }),
]);
export type StateOperation = z.infer<typeof StateOperation>;
export const StatePatchRequest = z.object({
  operations: z.array(z.object({ operationId: z.uuid(), baseRevision: z.number().int().nonnegative(), operation: StateOperation })).min(1).max(100),
});
export type StatePatchRequest = z.infer<typeof StatePatchRequest>;
export const StatePatchResponse = z.object({
  revision: z.number().int(),
  results: z.array(z.object({
    operationId: z.string(),
    status: z.enum(['applied', 'duplicate', 'stale', 'rejected']),
    reason: z.string().nullable(),
  })),
  state: UserState,
});
export type StatePatchResponse = z.infer<typeof StatePatchResponse>;
// ---- Saved-item display cards (MYTV01) ----
export const LibraryItem = z.object({
  id: Id, kind: MediaKind, title: z.string().max(300), artworkId: Id.nullable(), year: z.number().int().nullable(),
  durationSec: z.number().int().nonnegative().nullable(), available: z.boolean(), sourceId: Id.nullable(),
  channelNumber: z.number().int().nullable(), seriesId: Id.nullable(), seriesTitle: z.string().max(300).nullable(),
  season: z.number().int().nonnegative().nullable(), number: z.number().int().nullable(), newEpisodes: z.number().int().nonnegative(),
});
export type LibraryItem = z.infer<typeof LibraryItem>;
export const LibraryItemsResponse = z.object({ items: z.array(LibraryItem) });
export const PinRequest = z.object({ action: z.enum(['set', 'verify', 'clear', 'lock']), pin: z.string().regex(/^\d{4,8}$/).optional(), currentPin: z.string().optional() });

// ---- Playback (PLAY01-06) ----
export const PlaybackMode = z.enum(['live', 'vod', 'catchup', 'start_over']);
export const PlaybackRequest = z.object({
  sourceId: Id,
  mode: PlaybackMode,
  programId: Id.optional(),
  positionSec: z.number().nonnegative().optional(),
  tile: z.number().int().min(0).max(3).default(0).describe('Multiview tile index'),
  client: ClientCapabilities,
});
export type PlaybackRequest = z.infer<typeof PlaybackRequest>;
// ADR-0006: browser-direct by default (direct/external); the *_relay modes exist only when MEDIA_RELAY=on (off everywhere).
export const Delivery = z.enum(['direct', 'external', 'hls_relay', 'ts_relay', 'progressive_relay']);
export const PlaybackSession = z.object({
  sessionId: Id,
  playbackUrl: z.string().max(4096).describe('direct/external: the provider\'s credential-free token URL (ADR-0006); relay: /media/{sessionId}/{resourceId}. Never contains credentials.'),
  mimeType: z.string().max(100),
  engineHint: z.enum(['native', 'hlsjs', 'mpegts', 'progressive', 'convert', 'external']).describe('external = open in an external player (HTTP-only origin, ADR-0006). ' + 'mpegts = client-side TS transmux (mpegts.js); convert = client-side VOD audio conversion/remux (ADR-0004)'),
  delivery: Delivery,
  capabilities: z.object({
    seekable: z.boolean(),
    live: z.boolean(),
    startOver: z.boolean(),
    catchup: z.boolean(),
    pictureInPicture: z.boolean(),
  }),
  timeline: z.object({ programStart: IsoTime.nullable(), programEnd: IsoTime.nullable() }).nullable(),
  leaseExpiresAt: IsoTime,
  heartbeatIntervalSec: z.number().int(),
  sourceLabel: z.string().max(200),
  resumePositionSec: z.number().nonnegative().nullable(),
});
export type PlaybackSession = z.infer<typeof PlaybackSession>;
export const HeartbeatRequest = z.object({
  positionSec: z.number().nonnegative().optional(),
  state: z.enum(['playing', 'paused', 'buffering']).optional(),
});
export const HeartbeatResponse = z.object({ leaseExpiresAt: IsoTime });
export const ConnectionLimitDetails = z.object({
  inUse: z.number().int(),
  allowance: z.number().int(),
  allowanceSource: z.enum(['provider', 'user', 'default']),
  sessions: z.array(z.object({ sessionId: Id, label: z.string(), thisDevice: z.boolean(), startedAt: IsoTime })),
});

// ---- Diagnostics & data control (AUTH05, SAFE03, §12) ----
export const DiagnosticsResponse = z.object({
  generatedAt: IsoTime,
  app: z.object({ version: z.string(), environment: z.string() }),
  connections: z.array(z.object({
    id: Id, serverDisplay: z.string(), adapter: z.string(), account: z.unknown(),
    capabilities: z.array(Capability), catalog: z.unknown(),
  })),
  recentErrors: z.array(z.object({ at: IsoTime, code: z.string(), scope: z.string() })),
});
export type DiagnosticsResponse = z.infer<typeof DiagnosticsResponse>;
export const ExportDocument = z.object({
  format: z.literal('tuned.preferences'),
  version: z.literal(1),
  exportedAt: IsoTime,
  includesHistory: z.boolean(),
  profile: z.object({ name: z.string() }),
  preferences: Preferences,
  lists: z.array(z.object({ list: UserListName, kind: MediaKind, providerKey: z.string(), title: z.string().nullable() })),
  progress: z.array(z.object({ providerKey: z.string(), positionSec: z.number(), completed: z.boolean() })),
}).describe('Credential-free portable export (STORE04). providerKey = kind:providerItemId, never a URL');
export type ExportDocument = z.infer<typeof ExportDocument>;
export const ImportPreviewResponse = z.object({
  lists: z.number().int(), progress: z.number().int(), preferences: z.boolean(), unmatched: z.number().int(),
});
export type ImportPreviewResponse = z.infer<typeof ImportPreviewResponse>;

// ---- Route table: drives OpenAPI generation and the web client ----
export type Route = {
  method: 'get' | 'post' | 'patch' | 'delete' | 'head';
  path: string;
  summary: string;
  requirements: string[];
  query?: z.ZodObject;
  body?: z.ZodType;
  response?: z.ZodType;
  stateChanging: boolean;
};

export const routes: Route[] = [
  { method: 'get', path: '/api/session', summary: 'Bootstrap device workspace and connections', requirements: ['AUTH04'], response: SessionResponse, stateChanging: false },
  { method: 'post', path: '/api/session/logout', summary: 'Sign out of this device', requirements: ['AUTH05'], stateChanging: true },
  { method: 'delete', path: '/api/workspace', summary: 'Delete workspace and all data', requirements: ['SAFE03'], stateChanging: true },
  { method: 'get', path: '/api/connections', summary: 'List sources', requirements: ['AUTH02'], response: ConnectionListResponse, stateChanging: false },
  { method: 'post', path: '/api/connections', summary: 'Validate credentials and create or reconnect a source', requirements: ['AUTH01', 'AUTH02', 'AUTH03'], body: ConnectRequest, response: ConnectResponse, stateChanging: true },
  { method: 'get', path: '/api/connections/{id}', summary: 'Read connection and capabilities', requirements: ['XC05'], response: z.object({ connection: Connection }), stateChanging: false },
  { method: 'patch', path: '/api/connections/{id}', summary: 'Rename or set confirmed allowance', requirements: ['PLAY05'], body: UpdateConnectionRequest, response: z.object({ connection: Connection }), stateChanging: true },
  { method: 'delete', path: '/api/connections/{id}', summary: 'Forget connection (deletes vaulted credentials)', requirements: ['AUTH05'], stateChanging: true },
  { method: 'post', path: '/api/connections/{id}/disconnect', summary: 'Revoke playback and session credentials, keep preferences', requirements: ['AUTH05'], stateChanging: true },
  { method: 'post', path: '/api/connections/{id}/refresh', summary: 'Queue coalesced refresh', requirements: ['XC03', 'CF03'], response: RefreshResponse, stateChanging: true },
  { method: 'get', path: '/api/connections/{id}/epg-mappings', summary: 'Manual guide mappings and matching XMLTV channels', requirements: ['EPG02'], query: EpgMappingsQuery, response: EpgMappingsResponse, stateChanging: false },
  { method: 'patch', path: '/api/connections/{id}/epg-mappings', summary: 'Set a channel\'s manual guide mapping (exact XMLTV id)', requirements: ['EPG02', 'SAFE02'], body: EpgMapping, response: EpgMappingResponse, stateChanging: true },
  { method: 'delete', path: '/api/connections/{id}/epg-mappings/{channelId}', summary: 'Clear a manual guide mapping (back to the provider mapping)', requirements: ['EPG02', 'SAFE02'], stateChanging: true },
  { method: 'get', path: '/api/categories', summary: 'Categories for a kind', requirements: ['VOD01'], query: z.object({ connectionId: Id, kind: CatalogKind }), response: CategoriesResponse, stateChanging: false },
  { method: 'get', path: '/api/catalog', summary: 'Paged catalog bound to snapshot revision', requirements: ['XC04', 'VOD01'], query: CatalogQuery, response: CatalogResponse, stateChanging: false },
  { method: 'get', path: '/api/channels/{id}', summary: 'Channel detail', requirements: ['EPG01'], response: ChannelResponse, stateChanging: false },
  { method: 'get', path: '/api/movies/{id}', summary: 'Movie detail (loaded on demand)', requirements: ['VOD01'], response: MovieDetailResponse, stateChanging: false },
  { method: 'get', path: '/api/series/{id}', summary: 'Series detail and episodes', requirements: ['VOD02'], response: SeriesDetailResponse, stateChanging: false },
  { method: 'get', path: '/api/guide', summary: 'Bounded guide window', requirements: ['EPG01', 'EPG02', 'EPG03'], query: GuideQuery, response: GuideResponse, stateChanging: false },
  { method: 'get', path: '/api/search', summary: 'Grouped global search', requirements: ['SEARCH01'], query: SearchQuery, response: SearchResponse, stateChanging: false },
  { method: 'get', path: '/api/home', summary: 'Deterministic home sections', requirements: ['HOME01'], query: z.object({ connectionId: Id }), response: HomeResponse, stateChanging: false },
  { method: 'get', path: '/api/sports', summary: 'Today\'s games (every league, or one) with the channels carrying each one', requirements: ['SPORT01'], query: SportsQuery, response: SportsResponse, stateChanging: false },
  { method: 'get', path: '/api/profile/state', summary: 'Read profile state', requirements: ['STORE03'], response: UserState, stateChanging: false },
  { method: 'patch', path: '/api/profile/state', summary: 'Apply idempotent state operations', requirements: ['STORE03'], body: StatePatchRequest, response: StatePatchResponse, stateChanging: true },
  { method: 'get', path: '/api/profile/items', summary: 'Display info for saved items (≤100 ids)', requirements: ['MYTV01'], query: z.object({ ids: z.string().max(12900) }), response: LibraryItemsResponse, stateChanging: false },
  { method: 'post', path: '/api/profile/pin', summary: 'Set, verify, clear or lock the profile PIN', requirements: ['SAFE01'], body: PinRequest, response: Profile, stateChanging: true },
  { method: 'post', path: '/api/playback', summary: 'Authorize, reserve lease, return session', requirements: ['PLAY01', 'PLAY05'], body: PlaybackRequest, response: PlaybackSession, stateChanging: true },
  { method: 'post', path: '/api/playback/{id}/heartbeat', summary: 'Renew owned session', requirements: ['PLAY05'], body: HeartbeatRequest, response: HeartbeatResponse, stateChanging: true },
  { method: 'delete', path: '/api/playback/{id}', summary: 'Close owned session and release lease', requirements: ['PLAY05'], stateChanging: true },
  { method: 'get', path: '/media/{sessionId}/{resourceId}', summary: 'Authenticated manifest/segment/subtitle/key/file', requirements: ['PLAY02'], stateChanging: false },
  { method: 'head', path: '/media/{sessionId}/{resourceId}', summary: 'Authenticated resource metadata', requirements: ['PLAY02'], stateChanging: false },
  { method: 'get', path: '/api/artwork/{id}', summary: 'Validated raster artwork relay', requirements: ['SAFE02'], stateChanging: false },
  { method: 'get', path: '/api/diagnostics', summary: 'User diagnostic report without secrets', requirements: ['AUTH06'], response: DiagnosticsResponse, stateChanging: false },
  { method: 'get', path: '/api/export', summary: 'Credential-free preference export', requirements: ['STORE04', 'SAFE03'], query: z.object({ includeHistory: z.enum(['true', 'false']).default('false') }), response: ExportDocument, stateChanging: false },
  { method: 'post', path: '/api/import/preview', summary: 'Preview an export before import', requirements: ['STORE04'], body: ExportDocument, response: ImportPreviewResponse, stateChanging: true },
  { method: 'post', path: '/api/import', summary: 'Import a preference export', requirements: ['STORE04'], body: ExportDocument, response: ImportPreviewResponse, stateChanging: true },
];

/** Header every state-changing request must carry (ADR-0003, AUTH06). */
export const CSRF_HEADER = 'x-tuned-csrf';
export const CSRF_VALUE = '1';
